/* eslint-disable import/no-cycle */
import React, {useState} from 'react';
import {useTranslation} from 'react-i18next';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faListAlt} from '@fortawesome/free-solid-svg-icons';
import {observer} from 'mobx-react-lite';
import {Col, Row} from 'antd';
import Modal from '../../shared/modal/Modal';
import {useStore} from '../../../hooks/useStore';
import InspectionTaskDeactivationModal from './InspectionTaskDeactivationModal';
import {useMount} from '../../../hooks/useMount';
import {CustomPropertyTypes} from '../../../models/customProperty';
import GeneralDescription from './sections/GeneralDescription';
import MESDescription from './sections/MESDescription';
import TimesAndIntervalsDescription from './sections/TimesAndIntervalsDescription';
import SectionDescription from './sections/SectionDescription';
import PropertiesDescription from './sections/PropertiesDescription';
import BlameDescription from './sections/BlameDescription';
import ResultsDescription from './sections/ResultsDescription';
import QuantitativeSettingDescription from './sections/QuantitativeSettingDescription';
import QualitativeDescription from './sections/QualitativeDescription';
import InspectionTaskQuantitativeTargetValuesModal from './InspectionTaskQuantitativeTargetValuesModal';
import InspectionTaskQualitativeTargetValuesModal from './InspectionTaskQualitativeTargetValuesModal';
import {EnDash} from '../../shared/unicodeWrapper/EnDash';
import {useModulePolicy} from '../../../hooks/useModulePolicy';
import {taskManagement} from '../../../models/scope';

const InspectionTaskDrilldownModal = ({visible = true, task, onClose, ...props}) => {
  const {t} = useTranslation();
  const store = useStore();
  const [deactivateModalVisible, setDeactivateModalVisible] = useState(false);
  const [quantitativeTargetValueModalVisible, setQuantitativeTargetValueModalVisible] = useState(false);
  const [qualitativeTargetValueModalVisible, setQualitativeTargetValueModalVisible] = useState(false);
  const policy = useModulePolicy(store, [taskManagement]);

  const colProps = {lg: 8, md: 12, sm: 24};
  const fallback = (value) => (value === undefined ? EnDash() : value);
  const availableProperties = store.propertyStore.getByType({type: CustomPropertyTypes.InspectionTask});

  useMount(() => {
    store.documentStore.loadMany(task.documentIds);
  });

  return (
    <>
      <Modal
        width={'90%'}
        fullscreen={store.clientStore.isMobile}
        onCancel={onClose}
        bodyStyle={{minHeight: '420px'}}
        title={(
          <>
            <FontAwesomeIcon icon={faListAlt}/>
            {' '}
            {t('inspectionTask.drilldownModal.title')}
          </>
        )}
        visible={visible}
        footer={false}
        {...props}
      >
        <Row gutter={16}>
          <Col {...colProps}>
            <GeneralDescription fallback={fallback} task={task}/>
          </Col>
          <Col {...colProps}>
            <MESDescription fallback={fallback} task={task}/>
          </Col>
          <Col {...colProps}>
            <TimesAndIntervalsDescription fallback={fallback} task={task}/>
          </Col>

          <Col {...colProps}>
            <SectionDescription task={task}/>
          </Col>

          {availableProperties.length > 0 ? (
            <Col {...colProps}>
              <PropertiesDescription task={task} availableProperties={availableProperties}/>
            </Col>
          ) : null}

          {task.quantitative && (
            <Col {...colProps}>
              <QuantitativeSettingDescription
                canEdit={policy.canEdit()}
                task={task}
                fallback={fallback}
                setQuantitativeTargetValueModalVisible={setQuantitativeTargetValueModalVisible}
              />
            </Col>
          )}

          {task.qualitative?.catalogFeatures && (
            <Col {...colProps}>
              <QualitativeDescription
                canEdit={policy.canEdit()}
                task={task}
                setQualitativeTargetValueModalVisible={setQualitativeTargetValueModalVisible}
              />
            </Col>
          )}

          <Col {...colProps}>
            <BlameDescription fallback={fallback} task={task} setDeactivateModalVisible={setDeactivateModalVisible}/>
          </Col>

          <Col span={24}>
            <ResultsDescription task={task}/>
          </Col>
        </Row>
      </Modal>
      {deactivateModalVisible
      && <InspectionTaskDeactivationModal task={task} onClose={() => setDeactivateModalVisible(false)}/>}
      {quantitativeTargetValueModalVisible
      && (
        <InspectionTaskQuantitativeTargetValuesModal
          task={task}
          onClose={() => setQuantitativeTargetValueModalVisible(false)}
        />
      )}
      {qualitativeTargetValueModalVisible
      && (
        <InspectionTaskQualitativeTargetValuesModal
          task={task}
          onClose={() => setQualitativeTargetValueModalVisible(false)}
        />
      )}
    </>
  );
};

export default observer(InspectionTaskDrilldownModal);
